// src/features/booking/RescheduleDialog.tsx
// Move a confirmed booking to a new time slot.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { FormDialog, AutoGrowTextarea, FormRow } from '@/components/form';
import { dateTimeToIso, errMessage, toDateInputValue, toTimeInputValue } from './lib';
import { RescheduleBooking } from '@core/api/endpoints/booking/reschedule_booking';
import type { Booking } from '@core/types/booking';

interface Props {
  open: boolean;
  onClose: () => void;
  booking: Booking | null;
  onRescheduled: () => void;
}

export function RescheduleDialog({ open, onClose, booking, onRescheduled }: Props) {
  const { t } = useI18n();
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const now = new Date();
    setDate(toDateInputValue(now));
    setStartTime(toTimeInputValue(now));
    setEndTime(toTimeInputValue(new Date(now.getTime() + 3600000)));
    setReason('');
    setError(null);
  }, [open]);

  const submit = async () => {
    if (!booking?.entity) return;
    setSaving(true);
    setError(null);
    try {
      await RescheduleBooking(booking.entity.type_name || 'booking', booking.entity.id, {
        target: booking.entity,
        new_start_time: dateTimeToIso(date, startTime),
        new_end_time: dateTimeToIso(date, endTime),
        reason: reason || undefined,
      });
      setSaving(false);
      onRescheduled();
      onClose();
    } catch (e) {
      setSaving(false);
      setError(errMessage(e, t('booking.reschedule.error')));
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('booking.reschedule.confirm')}
      canSubmit={!!date && !!startTime && !!endTime}
      isSubmitting={saving}
      dirty={!!reason.trim()}
      aria-label={t('booking.reschedule.title')}
      width="md"
    >
      <div className="space-y-2 px-3.5 pb-2 pt-3">
        {error && <Alert variant="error">{error}</Alert>}
        <FormRow label={t('booking.reschedule.date')} labelWidth="sm" htmlFor="reschedule-date">
          <input
            id="reschedule-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
        <FormRow label={t('booking.reschedule.startTime')} labelWidth="sm" htmlFor="reschedule-start">
          <input
            id="reschedule-start"
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className="rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
        <FormRow label={t('booking.reschedule.endTime')} labelWidth="sm" htmlFor="reschedule-end">
          <input
            id="reschedule-end"
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            className="rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
        <AutoGrowTextarea value={reason} onChange={setReason} placeholder={`${t('booking.reschedule.reason')}…`} minRows={1} />
      </div>
    </FormDialog>
  );
}
